import { type QueryClient, type UseQueryOptions, queryOptions, useQuery } from '@tanstack/react-query';

import { getMeV1MeGet } from '../../generated';
import { mapMeContext } from './map-me-context';
import type { MeContext, MeContextWire } from './types';

export type GetMeContextQuery = MeContext;

const QUERY_KEY_BASE = 'GetMeContext';

// User-scoped so switching accounts (SPA login, no full reload) refetches from the backend
// instead of serving the previously cached user's context under staleTime: Infinity.
const meContextKey = (userId?: string | null) => [QUERY_KEY_BASE, userId ?? 'anonymous'] as const;

async function fetchMeContext(): Promise<MeContext> {
  const { data } = await getMeV1MeGet({
    throwOnError: true,
  });
  // MeContextView matches MeContextWire; keep local wire type for mapper stability.
  return mapMeContext(data as MeContextWire);
}

// Boot query: cache forever for the session; never refetch on focus.
const ME_CONTEXT_CACHE_POLICY = {
  staleTime: Number.POSITIVE_INFINITY,
  refetchOnWindowFocus: false,
} as const;

function meContextQueryOptions(userId?: string | null) {
  return queryOptions({
    queryKey: meContextKey(userId),
    queryFn: fetchMeContext,
    ...ME_CONTEXT_CACHE_POLICY,
  });
}

export const useGetMeContextQuery = <TData = GetMeContextQuery, TError = Error>(
  userId?: string | null,
  options?: Omit<UseQueryOptions<GetMeContextQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetMeContextQuery, TError, TData>({
    queryKey: meContextKey(userId),
    queryFn: fetchMeContext,
    ...ME_CONTEXT_CACHE_POLICY,
    ...options,
  });
};

// Prefix key for invalidation — matches every user-scoped entry.
useGetMeContextQuery.getKey = () => [QUERY_KEY_BASE] as const;

export async function ensureMeContext(queryClient: QueryClient, userId?: string | null): Promise<MeContext> {
  return queryClient.ensureQueryData(meContextQueryOptions(userId));
}
